import { Link } from 'react-router-dom';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Icon } from '../../components/ui/Icon';
import { PLANS, FREE_PLAN, designManifest, formatPlanPrice } from '@sarnia/shared';
import { useOptionalAuth } from '../../stores/auth';
import './pricing.css';

export function PricingPage() {
  const auth = useOptionalAuth();
  const signedIn = Boolean(auth?.user);

  function planHref(planId: string): string {
    return signedIn ? `/dashboard/account/billing?plan=${planId}` : `/signup?plan=${planId}`;
  }

  return (
    <PublicLayout>
      <section className="pricing-hero">
        <div className="sarnia-container pricing-hero__inner">
          <span className="sarnia-badge">Free to use. Pay only for our help.</span>
          <h1>Everything is free. You only pay if you want us to handle setup and maintenance.</h1>
          <p>
            Build as many sites as you like, edit everything, share live previews and export your finished
            website: no card, no time limit, no catch. Choose a plan only if you'd rather Sarnia Digital got
            it live on your own domain and looked after it for you.
          </p>
        </div>
      </section>

      <section className="pricing-grid-section">
        <div className="sarnia-container pricing-card-grid">
          <article className="pricing-card">
            <div className="pricing-card__header">
              <h2>{FREE_PLAN.name}</h2>
              <p className="pricing-card__tagline">{FREE_PLAN.tagline}</p>
              <div className="pricing-card__price">
                <span>£0</span>
                <span className="pricing-card__period">forever</span>
              </div>
            </div>
            <ul className="pricing-card__features">
              {FREE_PLAN.features.map((feature) => (
                <li key={feature}>
                  <Icon name="check" size={18} className="pricing-check" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <Link className="sarnia-btn sarnia-btn--secondary sarnia-btn--lg" to="/signup">
              Start building
            </Link>
          </article>

          {PLANS.map((plan) => (
            <article
              key={plan.id}
              className={`pricing-card${plan.highlight ? ' pricing-card--highlight' : ''}`}
            >
              {plan.highlight && <span className="pricing-card__ribbon">Most popular</span>}
              <div className="pricing-card__header">
                <h2>{plan.name}</h2>
                <p className="pricing-card__tagline">{plan.tagline}</p>
                <div className="pricing-card__price">
                  <span>{formatPlanPrice(plan)}</span>
                  <span className="pricing-card__period">/{plan.interval}</span>
                </div>
              </div>
              <ul className="pricing-card__features">
                {plan.features.map((feature) => (
                  <li key={feature}>
                    <Icon name="check" size={18} className="pricing-check" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Link
                className={`sarnia-btn sarnia-btn--lg ${
                  plan.highlight ? 'sarnia-btn--primary' : 'sarnia-btn--secondary'
                }`}
                to={planHref(plan.id)}
              >
                Choose {plan.name}
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="pricing-how">
        <div className="sarnia-container">
          <div className="pricing-how__header">
            <h2>How it works</h2>
            <p>Designed for people who want a beautiful website without the technical run-around.</p>
          </div>
          <ol className="pricing-how__steps">
            <li>
              <span className="pricing-how__step-number" aria-hidden="true">1</span>
              <div>
                <h3>Design it in the editor</h3>
                <p>Start from a template or a blank canvas. Use the visual block editor, add your images and content, and see the exact result as you work.</p>
              </div>
            </li>
            <li>
              <span className="pricing-how__step-number" aria-hidden="true">2</span>
              <div>
                <h3>Share a preview link with anyone</h3>
                <p>Every site has a live preview link, clearly marked as a preview and not indexed by search engines. Perfect for design feedback or client sign-off.</p>
              </div>
            </li>
            <li>
              <span className="pricing-how__step-number" aria-hidden="true">3</span>
              <div>
                <h3>Export the ZIP, or let us take it live and keep it running</h3>
                <p>Export a clean ZIP to host anywhere. Or upgrade to Basic or Premium and our team in Guernsey will handle domain, hosting and ongoing maintenance.</p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      <section className="pricing-split">
        <div className="sarnia-container pricing-split__inner">
          <h2>How "free" works</h2>
          <div className="pricing-split__grid">
            <article className="pricing-split__card">
              <p className="pricing-split__eyebrow">Always free</p>
              <h3>Everything you do yourself</h3>
              <ul>
                <li>The whole editor and all {designManifest.length} designer templates</li>
                <li>Your own images, colours, fonts, links and animated backgrounds</li>
                <li>Shareable live previews to get feedback</li>
                <li>Exporting your finished website as a ZIP, to host anywhere you like</li>
              </ul>
            </article>
            <article className="pricing-split__card pricing-split__card--paid">
              <p className="pricing-split__eyebrow">Pay only for our help</p>
              <h3>When you want us to do it for you</h3>
              <ul>
                <li>We set your site live on your own domain</li>
                <li>We look after hosting and keep it running</li>
                <li>Ongoing maintenance and updates, with support from real people</li>
                <li>From {formatPlanPrice(PLANS[0]!)}/month, cancel any time</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section className="pricing-faq">
        <div className="sarnia-container pricing-faq__inner">
          <h2>Questions, answered honestly</h2>
          <div className="pricing-faq__list">
            <details open>
              <summary>Is it really free?</summary>
              <p>Yes. Building, editing, previewing and exporting your website is free, with no card and no time limit. The only thing that costs money is our hands-on service: if you want Sarnia Digital to do the setup and the ongoing maintenance for you, that's Basic or Premium. If you'd rather do it yourself, export the ZIP and host it wherever you like.</p>
            </details>
            <details>
              <summary>Is the preview my real website?</summary>
              <p>No. It's a marked preview for sharing and gathering feedback. Search engines are told not to index it, so it isn't meant to be used as your real public website.</p>
            </details>
            <details>
              <summary>What do I get with Basic and Premium?</summary>
              <p>Both plans include hands-on help from Sarnia Digital. We'll get your site live on your own domain, host it, and keep it maintained. Premium adds priority setup, priority support, and more hands-on content updates.</p>
            </details>
            <details>
              <summary>Do I have to subscribe?</summary>
              <p>No. You can export the ZIP at any time and host it yourself. Subscribing is only for when you want Sarnia Digital to take it live and keep it running for you.</p>
            </details>
            <details>
              <summary>What's in the ZIP?</summary>
              <p>The exported ZIP contains your complete website: HTML, CSS, JavaScript and all imported images and assets. It's ready to upload to any static host.</p>
            </details>
            <details>
              <summary>Can I cancel?</summary>
              <p>Yes, anytime. If you cancel, you'll keep your exported site, and your subscription will end at the end of the current billing period.</p>
            </details>
            <details>
              <summary>Who builds and maintains it?</summary>
              <p>Sarnia Digital, the Guernsey studio behind Sarnia Builder. Small island. Big signal.</p>
            </details>
          </div>
        </div>
      </section>

      <section className="pricing-footer-cta">
        <div className="sarnia-container pricing-footer-cta__inner">
          <h2>Start your site today</h2>
          <p>Design it free. Preview it free. Export it free. Pay only when you want Sarnia Digital to take it live.</p>
          <Link className="sarnia-btn sarnia-btn--primary sarnia-btn--lg" to="/signup">
            Start building
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
